/**
 * Account Form Page
 * 
 * Create or edit a bank account.
 * 
 * @validates Requirements 4.1, 4.2, 4.3 - Account management
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAccountStore } from '../store/accountStore';
import { useCategoryStore } from '../store/categoryStore';
import { usePaymentMethodStore } from '../store/paymentMethodStore';
import { Card, Button, Input } from '../components/ui';
import { BankIcon } from '../components/ui/BankIcon';
import { api } from '../services/api';
import { 
  Currency, 
  InterestCalculationMethod, 
  InterestPaymentFrequency,
  INTEREST_CALCULATION_METHODS,
  INTEREST_PAYMENT_FREQUENCIES,
  Counterparty,
} from '@budgira/shared';
import { getBankNameFromIBAN } from '../utils/ibanBankMapping';
import { InterestRateHistory } from '../components/InterestRateHistory';

interface AccountFormData {
  name: string;
  bankName: string;
  iban: string;
  currency: Currency;
  openingDate: string;
  initialBalance: string;
  interestRate: string;
  interestCalculationMethod: InterestCalculationMethod | '';
  interestPaymentFrequency: InterestPaymentFrequency | '';
  interestCategoryId: string;
  interestCounterpartyId: string;
  interestPaymentMethodId: string;
}

const CURRENCIES: Currency[] = ['EUR', 'USD', 'GBP'];

const BANKS = [
  'Banque Populaire',
  'BNP Paribas',
  'Boursorama',
  'Caisse d\'Épargne',
  'Crédit Agricole',
  'Crédit Coopératif',
  'Crédit Mutuel',
  'Fortuneo',
  'Hello Bank',
  'HSBC',
  'Indy',
  'ING',
  'La Banque Postale',
  'LCL',
  'Milleis',
  'Monabanq',
  'N26',
  'Nickel',
  'Qonto',
  'Revolut',
  'Société Générale',
  'Autre',
];

const AccountFormPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accountId } = useParams<{ accountId?: string }>();
  const { accounts, createAccount, updateAccount, fetchAccounts, error } = useAccountStore();
  const { categories, fetchCategories } = useCategoryStore();
  const { paymentMethods, fetchPaymentMethods } = usePaymentMethodStore();
  const [counterparties, setCounterparties] = useState<Counterparty[]>([]);
  
  const isEditMode = accountId && accountId !== 'new';
  const existingAccount = isEditMode ? accounts.find(a => a.id === accountId) : null;

  const [formData, setFormData] = useState<AccountFormData>({
    name: '',
    bankName: '',
    iban: '',
    currency: 'EUR',
    openingDate: '',
    initialBalance: '0',
    interestRate: '',
    interestCalculationMethod: '',
    interestPaymentFrequency: '',
    interestCategoryId: '',
    interestCounterpartyId: '',
    interestPaymentMethodId: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (accounts.length === 0) {
      fetchAccounts();
    }
    // Fetch categories and payment methods for interest defaults
    if (categories.length === 0) {
      fetchCategories();
    }
    if (paymentMethods.length === 0) {
      fetchPaymentMethods();
    }
    // Fetch counterparties via API
    const loadCounterparties = async () => {
      try {
        const response = await api.get<{ success: boolean; data: Counterparty[] }>('/counterparties');
        if (response.data.success) {
          setCounterparties(response.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch counterparties:', err);
      }
    };
    loadCounterparties();
  }, [accounts.length, categories.length, paymentMethods.length, fetchAccounts, fetchCategories, fetchPaymentMethods]);

  useEffect(() => {
    if (existingAccount) {
      setFormData({
        name: existingAccount.name,
        bankName: existingAccount.bankName,
        iban: existingAccount.iban || '',
        currency: existingAccount.currency,
        openingDate: existingAccount.openingDate || '',
        initialBalance: (existingAccount.initialBalance || 0).toString(),
        interestRate: existingAccount.interestRate?.toString() || '',
        interestCalculationMethod: existingAccount.interestCalculationMethod || '',
        interestPaymentFrequency: existingAccount.interestPaymentFrequency || '',
        interestCategoryId: existingAccount.interestCategoryId || '',
        interestCounterpartyId: existingAccount.interestCounterpartyId || '',
        interestPaymentMethodId: existingAccount.interestPaymentMethodId || '',
      });
    }
  }, [existingAccount]);

  // Auto-select counterparty matching bank name when interest settings are shown
  useEffect(() => {
    // Only auto-select if:
    // - Interest is being configured (rate + method)
    // - No counterparty is currently selected
    // - We have counterparties loaded
    // - We have a bank name
    if (
      formData.interestCalculationMethod &&
      formData.interestPaymentFrequency &&
      !formData.interestCounterpartyId &&
      counterparties.length > 0 &&
      formData.bankName
    ) {
      // Find counterparty matching bank name (case-insensitive)
      const matchingCounterparty = counterparties.find(
        cp => cp.name.toLowerCase() === formData.bankName.toLowerCase()
      );
      if (matchingCounterparty) {
        setFormData(prev => ({ ...prev, interestCounterpartyId: matchingCounterparty.id }));
      }
    }
  }, [formData.interestCalculationMethod, formData.interestPaymentFrequency, formData.bankName, counterparties, formData.interestCounterpartyId]);

  const validateIBAN = (iban: string): boolean => {
    // Basic IBAN format validation (simplified)
    const cleanIban = iban.replace(/\s/g, '').toUpperCase();
    if (cleanIban.length < 15 || cleanIban.length > 34) return false;
    // Check starts with 2 letters
    if (!/^[A-Z]{2}/.test(cleanIban)) return false;
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Validation
    if (!formData.name.trim()) {
      setFormError(t('accounts.form.errors.nameRequired'));
      return;
    }
    if (!formData.bankName.trim()) {
      setFormError(t('accounts.form.errors.bankRequired'));
      return;
    }
    if (!formData.iban.trim()) {
      setFormError(t('accounts.form.errors.ibanRequired'));
      return;
    }
    if (!validateIBAN(formData.iban)) {
      setFormError(t('accounts.form.errors.ibanInvalid'));
      return;
    }

    setIsSaving(true);
    try {
      const initialBalanceValue = parseFloat(formData.initialBalance) || 0;
      const interestRateValue = formData.interestRate ? parseFloat(formData.interestRate) : undefined;
      
      // If interest is configured but no counterparty selected, create one with bank name
      let counterpartyId = formData.interestCounterpartyId || undefined;
      if (
        formData.interestCalculationMethod &&
        formData.interestPaymentFrequency &&
        !counterpartyId &&
        formData.bankName
      ) {
        // Check if counterparty with bank name exists
        const existingCp = counterparties.find(
          cp => cp.name.toLowerCase() === formData.bankName.toLowerCase()
        );
        if (existingCp) {
          counterpartyId = existingCp.id;
        } else {
          // Create new counterparty with bank name
          try {
            const response = await api.post<{ success: boolean; data: Counterparty }>('/counterparties', {
              name: formData.bankName,
            });
            if (response.data.success && response.data.data) {
              counterpartyId = response.data.data.id;
              // Update local list
              setCounterparties(prev => [...prev, response.data.data]);
            }
          } catch (err) {
            console.error('Failed to create counterparty:', err);
            // Continue without counterparty - user can set it later
          }
        }
      }
      
      let success = false;
      if (isEditMode && accountId) {
        const result = await updateAccount(accountId, {
          name: formData.name.trim(),
          bankName: formData.bankName.trim(),
          iban: formData.iban.replace(/\s/g, '').toUpperCase(),
          currency: formData.currency,
          openingDate: formData.openingDate || undefined,
          initialBalance: initialBalanceValue,
          interestRate: interestRateValue ?? null,
          interestCalculationMethod: formData.interestCalculationMethod || null,
          interestPaymentFrequency: formData.interestPaymentFrequency || null,
          interestCategoryId: formData.interestCategoryId || null,
          interestCounterpartyId: counterpartyId || null,
          interestPaymentMethodId: formData.interestPaymentMethodId || null,
        });
        success = result !== null;
      } else {
        const result = await createAccount({
          name: formData.name.trim(),
          bankName: formData.bankName.trim(),
          iban: formData.iban.replace(/\s/g, '').toUpperCase(),
          currency: formData.currency,
          openingDate: formData.openingDate || undefined,
          initialBalance: initialBalanceValue,
          interestRate: interestRateValue ?? null,
          interestCalculationMethod: formData.interestCalculationMethod || null,
          interestPaymentFrequency: formData.interestPaymentFrequency || null,
          interestCategoryId: formData.interestCategoryId || null,
          interestCounterpartyId: counterpartyId || null,
          interestPaymentMethodId: formData.interestPaymentMethodId || null,
        });
        success = result !== null;
      }
      
      if (success) {
        navigate('/accounts');
      }
      // If not success, error will be shown from store
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: keyof AccountFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setFormError(null);
    
    // Auto-detect bank from IBAN
    if (field === 'iban' && value.length >= 9) {
      const detectedBank = getBankNameFromIBAN(value);
      // Si banque détectée, l'utiliser, sinon mettre "Autre"
      const bankToSet = detectedBank || 'Autre';
      setFormData(prev => ({ ...prev, [field]: value, bankName: bankToSet }));
    }
  };

  // Format IBAN as user types (add spaces every 4 characters)
  const formatIBAN = (value: string): string => {
    const clean = value.replace(/\s/g, '').toUpperCase();
    const groups = clean.match(/.{1,4}/g);
    return groups ? groups.join(' ') : clean;
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          {isEditMode ? t('accounts.form.editTitle') : t('accounts.form.createTitle')}
        </h1>
        <p className="text-slate-500 dark:text-slate-400">
          {isEditMode ? t('accounts.form.editSubtitle') : t('accounts.form.createSubtitle')}
        </p>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error message */}
          {(formError || error) && (
            <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-red-600 dark:text-red-400">
              {formError || error}
            </div>
          )}

          {/* Account Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('accounts.form.name')} *
            </label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('accounts.form.namePlaceholder')}
              disabled={isSaving}
            />
          </div>

          {/* IBAN - moved before Bank Name for auto-detection */}
          <div>
            <label htmlFor="iban" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('accounts.form.iban')} *
            </label>
            <Input
              id="iban"
              type="text"
              value={formatIBAN(formData.iban)}
              onChange={(e) => handleChange('iban', e.target.value.replace(/\s/g, ''))}
              placeholder="FR76 1234 5678 9012 3456 7890 123"
              disabled={isSaving}
              className="font-mono"
            />
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {t('accounts.form.ibanHelp')}
            </p>
          </div>

          {/* Bank Name with Icon */}
          <div>
            <label htmlFor="bankName" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('accounts.form.bankName')} *
            </label>
            <div className="flex items-center gap-3">
              {formData.bankName && (
                <BankIcon bankName={formData.bankName} size="lg" />
              )}
              <select
                id="bankName"
                value={formData.bankName}
                onChange={(e) => handleChange('bankName', e.target.value)}
                disabled={isSaving}
                className="flex-1 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
              >
                <option value="">{t('accounts.form.selectBank')}</option>
                {BANKS.map(bank => (
                  <option key={bank} value={bank}>{bank}</option>
                ))}
              </select>
            </div>
            {formData.iban.length >= 9 && getBankNameFromIBAN(formData.iban) && (
              <p className="mt-1 text-sm text-green-600 dark:text-green-400">
                {t('accounts.form.bankDetected', { bank: getBankNameFromIBAN(formData.iban) })}
              </p>
            )}
          </div>

          {/* Currency */}
          <div>
            <label htmlFor="currency" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('accounts.form.currency')} *
            </label>
            <select
              id="currency"
              value={formData.currency}
              onChange={(e) => handleChange('currency', e.target.value as Currency)}
              disabled={isSaving}
              className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
            >
              {CURRENCIES.map(currency => (
                <option key={currency} value={currency}>
                  {currency} - {t(`currencies.${currency}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Opening Date */}
          <div>
            <label htmlFor="openingDate" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('accounts.form.openingDate')}
            </label>
            <Input
              id="openingDate"
              type="date"
              value={formData.openingDate}
              onChange={(e) => handleChange('openingDate', e.target.value)}
              disabled={isSaving}
            />
          </div>

          {/* Initial Balance */}
          <div>
            <label htmlFor="initialBalance" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('accounts.form.initialBalance')}
            </label>
            <Input
              id="initialBalance"
              type="number"
              step="0.01"
              value={formData.initialBalance}
              onChange={(e) => handleChange('initialBalance', e.target.value)}
              placeholder="0.00"
              disabled={isSaving}
            />
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {t('accounts.form.initialBalanceHelp')}
            </p>
          </div>

          {/* Interest Settings Section */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-4">
              {t('accounts.form.interestSettings', 'Paramètres d\'intérêts')}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              {t('accounts.form.interestSettingsHelp', 'Configurez ces paramètres pour calculer les intérêts créditeurs de ce compte (livret, compte rémunéré...)')}
            </p>

            {/* Interest Rate - Different UI for edit vs create */}
            {isEditMode && accountId ? (
              /* In edit mode: show interest rate history component */
              <div className="mb-4">
                <InterestRateHistory
                  accountId={accountId}
                  onRatesChange={() => fetchAccounts()}
                  disabled={isSaving}
                />
              </div>
            ) : (
              /* In create mode: show similar UI with add rate button */
              <div className="mb-4 space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {t('accounts.interestRates.title', 'Historique des taux d\'intérêts')}
                  </h4>
                  {!formData.interestRate && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        // Set a default date based on opening date or today
                        const defaultDate = formData.openingDate || new Date().toISOString().split('T')[0] || '';
                        setFormData(prev => ({ 
                          ...prev, 
                          interestRate: '0',  // Will be replaced by user input
                          _rateStartDate: defaultDate 
                        } as AccountFormData & { _rateStartDate?: string }));
                      }}
                      disabled={isSaving}
                    >
                      {t('accounts.interestRates.addRate', 'Ajouter un taux')}
                    </Button>
                  )}
                </div>

                {/* Rate display or add form */}
                {formData.interestRate ? (
                  <div className="p-3 rounded-lg border border-violet-500 bg-violet-50 dark:bg-violet-900/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="text-lg font-semibold text-slate-900 dark:text-white">
                          {parseFloat(formData.interestRate).toFixed(2)}%
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          {t('accounts.interestRates.from', 'Du')} {formData.openingDate ? new Date(formData.openingDate).toLocaleDateString() : t('accounts.interestRates.accountCreation', 'Date d\'ouverture')}
                        </div>
                        <span className="px-2 py-0.5 text-xs font-medium bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded">
                          {t('accounts.interestRates.current', 'En cours')}
                        </span>
                      </div>
                      {!isSaving && (
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              // Re-open edit mode
                              setFormData(prev => ({ ...prev, interestRate: '' }));
                            }}
                          >
                            {t('common.edit', 'Modifier')}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 dark:text-red-400"
                            onClick={() => handleChange('interestRate', '')}
                          >
                            {t('common.delete', 'Supprimer')}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-slate-500 dark:text-slate-400 text-center py-4 border border-dashed border-slate-300 dark:border-slate-600 rounded-lg">
                    {t('accounts.interestRates.noRates', 'Aucun taux d\'intérêt configuré. Ajoutez un premier taux.')}
                  </div>
                )}

                {/* Add form - shown when interestRate is '0' (just clicked add button) */}
                {formData.interestRate === '0' && (
                  <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                          {t('accounts.interestRates.rate', 'Taux (%)')}
                        </label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value=""
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value && parseFloat(value) > 0) {
                              handleChange('interestRate', value);
                            }
                          }}
                          placeholder="3.00"
                          disabled={isSaving}
                          autoFocus
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                          {t('accounts.interestRates.startDate', 'Date de début')}
                        </label>
                        <Input
                          type="date"
                          value={formData.openingDate || ''}
                          onChange={(e) => handleChange('openingDate', e.target.value)}
                          disabled={isSaving}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => handleChange('interestRate', '')}
                        disabled={isSaving}
                      >
                        {t('common.cancel', 'Annuler')}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Help text */}
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t('accounts.interestRates.helpText', 'Lorsque vous ajoutez un nouveau taux, le taux précédent est automatiquement clôturé à la veille.')}
                </p>
              </div>
            )}

            {/* Interest Calculation Method */}
            <div className="mb-4">
              <label htmlFor="interestCalculationMethod" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('accounts.form.interestCalculationMethod', 'Méthode de calcul')}
              </label>
              <select
                id="interestCalculationMethod"
                value={formData.interestCalculationMethod}
                onChange={(e) => handleChange('interestCalculationMethod', e.target.value as InterestCalculationMethod | '')}
                disabled={isSaving}
                className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
              >
                <option value="">{t('accounts.form.selectCalculationMethod', 'Sélectionner une méthode')}</option>
                {INTEREST_CALCULATION_METHODS.map(method => (
                  <option key={method.value} value={method.value}>
                    {t(`accounts.form.calculationMethods.${method.value}`, method.value)}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {formData.interestCalculationMethod === 'daily' && t('accounts.form.calculationMethodHelp.daily', 'Intérêts calculés quotidiennement sur le solde du jour')}
                {formData.interestCalculationMethod === 'biweekly_strict' && t('accounts.form.calculationMethodHelp.biweekly_strict', 'Méthode française : dépôts comptent à la quinzaine suivante, retraits immédiats')}
                {formData.interestCalculationMethod === 'biweekly_simple' && t('accounts.form.calculationMethodHelp.biweekly_simple', 'Calcul sur le solde de fin de quinzaine (1er et 16 du mois)')}
                {!formData.interestCalculationMethod && t('accounts.form.calculationMethodHelp.none', 'Choisissez la méthode utilisée par votre banque')}
              </p>
            </div>

            {/* Interest Payment Frequency */}
            <div>
              <label htmlFor="interestPaymentFrequency" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('accounts.form.interestPaymentFrequency', 'Fréquence de versement')}
              </label>
              <select
                id="interestPaymentFrequency"
                value={formData.interestPaymentFrequency}
                onChange={(e) => handleChange('interestPaymentFrequency', e.target.value as InterestPaymentFrequency | '')}
                disabled={isSaving}
                className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
              >
                <option value="">{t('accounts.form.selectPaymentFrequency', 'Sélectionner une fréquence')}</option>
                {INTEREST_PAYMENT_FREQUENCIES.map(freq => (
                  <option key={freq.value} value={freq.value}>
                    {t(`accounts.form.paymentFrequencies.${freq.value}`, freq.value)}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {t('accounts.form.paymentFrequencyHelp', 'Date à laquelle les intérêts sont versés sur le compte')}
              </p>
            </div>

            {/* Interest Defaults Subsection - only show if interest is configured */}
            {formData.interestCalculationMethod && (
              <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-700/50">
                <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-3">
                  {t('accounts.form.interestDefaults', 'Paramètres par défaut pour les opérations d\'intérêts')}
                </h4>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                  {t('accounts.form.interestDefaultsHelp', 'Ces valeurs seront utilisées automatiquement lors de la création d\'opérations d\'intérêts.')}
                </p>

                {/* Interest Category */}
                <div className="mb-4">
                  <label htmlFor="interestCategoryId" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t('accounts.form.interestCategory', 'Catégorie')}
                  </label>
                  <select
                    id="interestCategoryId"
                    value={formData.interestCategoryId}
                    onChange={(e) => handleChange('interestCategoryId', e.target.value)}
                    disabled={isSaving}
                    className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
                  >
                    <option value="">{t('accounts.form.selectInterestCategory', 'Sélectionner une catégorie')}</option>
                    {categories
                      .filter(c => c.baseType === 'credit' && !c.parentId)
                      .map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                  </select>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    {t('accounts.form.interestCategoryHelp', 'Ex: Revenus financiers, Intérêts bancaires...')}
                  </p>
                </div>

                {/* Interest Counterparty */}
                <div className="mb-4">
                  <label htmlFor="interestCounterpartyId" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t('accounts.form.interestCounterparty', 'Tiers')}
                  </label>
                  <select
                    id="interestCounterpartyId"
                    value={formData.interestCounterpartyId}
                    onChange={(e) => handleChange('interestCounterpartyId', e.target.value)}
                    disabled={isSaving}
                    className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
                  >
                    <option value="">
                      {counterparties.some(cp => cp.name.toLowerCase() === formData.bankName.toLowerCase())
                        ? t('accounts.form.selectInterestCounterparty', 'Sélectionner un tiers')
                        : t('accounts.form.autoCreateCounterparty', 'Créer automatiquement "{{bank}}"', { bank: formData.bankName })
                      }
                    </option>
                    {counterparties.map(cp => (
                      <option key={cp.id} value={cp.id}>{cp.name}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    {!formData.interestCounterpartyId && !counterparties.some(cp => cp.name.toLowerCase() === formData.bankName.toLowerCase())
                      ? t('accounts.form.counterpartyWillBeCreated', 'Le tiers "{{bank}}" sera créé automatiquement', { bank: formData.bankName })
                      : t('accounts.form.interestCounterpartyHelp', 'Généralement votre banque')
                    }
                  </p>
                </div>

                {/* Interest Payment Method */}
                <div>
                  <label htmlFor="interestPaymentMethodId" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t('accounts.form.interestPaymentMethod', 'Mode de paiement')}
                  </label>
                  <select
                    id="interestPaymentMethodId"
                    value={formData.interestPaymentMethodId}
                    onChange={(e) => handleChange('interestPaymentMethodId', e.target.value)}
                    disabled={isSaving}
                    className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
                  >
                    <option value="">{t('accounts.form.selectInterestPaymentMethod', 'Sélectionner un mode')}</option>
                    {paymentMethods.map(pm => (
                      <option key={pm.id} value={pm.id}>{pm.name}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    {t('accounts.form.interestPaymentMethodHelp', 'Ex: Virement bancaire')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/accounts')}
              disabled={isSaving}
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="flex-1"
            >
              {isSaving ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('common.saving')}
                </span>
              ) : (
                isEditMode ? t('common.save') : t('accounts.form.create')
              )}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AccountFormPage;
